import * as Popover from '@radix-ui/react-popover';
import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { CustomField } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type {
    CalendarDateSourceWithAll,
    NormalisedCalendarConfig,
    PeriodDef,
} from './calendar-config';
import { CALENDAR_DATE_SOURCES } from '@/lib/schemas';
import { getColorByOptions } from './calendar-i18n';

// ============================================
// CALENDAR SETTINGS PANEL
// ============================================
//
// t16: popover with view mode + date source + color rule controls.
// Each change writes back via onConfigChange so the saved view's
// config stays in sync.
//
// T21 R4 restructure (per the R4 spec):
//   - REMOVED the "View mode" section. The view mode now lives only
//     in the toolbar's Month / Week / Agenda tab buttons — having it
//     here was redundant and the user explicitly asked for it gone.
//   - REMOVED the "Filter by status" section. Date filtering (the
//     only filter the Calendar ever needed) now happens in the global
//     filter bar at the top of the page.
//   - ADDED an "All tasks" option to the Date source selector. When
//     picked, the Calendar shows every task regardless of its date
//     columns. The implementation: a literal `'all'` source value
//     that bypasses `resolveTaskDate` (returns null → task omitted).
//   - ADDED a "Periods" section. The user can create named date
//     ranges (e.g. "Absence 12-10-2026 → 15-10-2026") which render as
//     sprint-band-style highlights behind each calendar day. Periods
//     persist in the active view's config under
//     `calendarSettings.periods`.
//
// The panel is rendered WITHOUT its own <Popover.Root> wrapper — the
// parent (CalendarView) owns the Root and the toolbar owns the
// Trigger. Popover.Content inside a Portal still positions against
// the Trigger via the PopperContext.

export interface CalendarSettingsPanelProps {
    config: NormalisedCalendarConfig;
    customFields: CustomField[];
    /** True if the project has at least one Sprint custom field. */
    hasSprintField: boolean;
    onConfigChange: (next: NormalisedCalendarConfig) => void;
}

export function CalendarSettingsPanel({
    config,
    customFields,
    hasSprintField,
    onConfigChange,
}: CalendarSettingsPanelProps) {
    const { t } = useI18n();
    const colorByOptions = useMemo(() => getColorByOptions(customFields), [customFields]);

    const isSprintSource = (src: string) => src === 'sprintStart' || src === 'sprintEnd';
    const sprintSourcesEnabled = hasSprintField;

    // The "All tasks" sentinel is appended in front of the canonical
    // date sources. We don't put it in CALENDAR_DATE_SOURCES itself
    // because that constant is shared with the backend view schema —
    // 'all' would fail server-side validation. Instead we add it as a
    // display-only prefix here.
    const dateSourceOptions: Array<{ value: CalendarDateSourceWithAll; label: string; disabled?: boolean }> = [
        { value: 'all', label: t('calendar.dateSource.all') },
        ...CALENDAR_DATE_SOURCES.map((src) => {
            const disabled = isSprintSource(src) && !sprintSourcesEnabled;
            return {
                value: src,
                label: t(`calendar.dateSource.${src}`),
                disabled,
            };
        }),
    ];

    return (
        <Popover.Portal>
            {/* T30 R6: responsive panel width. w-[calc(100vw-2rem)]
                caps the popover at the viewport minus 1rem margins on
                each side so it never overflows on phones (375px wide
                → 343px panel). sm:w-96 restores the desktop width.
                Floating UI's shift/size middleware handles the anchor
                alignment either way (Radix sets max-height/shift on
                its own). */}
            <Popover.Content
                align="end"
                sideOffset={6}
                className="z-50 w-[calc(100vw-2rem)] sm:w-96 max-h-[80vh] overflow-y-auto rounded-md border border-border bg-surface p-3 sm:p-4 shadow-lg space-y-4"
                data-testid="calendar-settings-panel"
            >
                <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">
                        {t('calendar.settingsTitle')}
                    </h4>
                    <p className="text-xs text-foreground-muted">
                        {t('calendar.settingsHint')}
                    </p>
                </div>

                {/* Date source. T21 R4: "All tasks" prefix lets the
                    user turn off date filtering entirely — date
                    filtering then moves to the global filter bar. */}
                <div className="space-y-2">
                    <Label htmlFor="calendar-dateSource" className="text-xs font-medium">
                        {t('calendar.dateSourceLabel')}
                    </Label>
                    <Select
                        value={config.dateSource}
                        onValueChange={(v) =>
                            onConfigChange({
                                ...config,
                                dateSource: v as CalendarDateSourceWithAll,
                            })
                        }
                    >
                        <SelectTrigger id="calendar-dateSource" className="h-8">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {dateSourceOptions.map((opt) => (
                                <SelectItem
                                    key={opt.value}
                                    value={opt.value}
                                    disabled={opt.disabled}
                                >
                                    {opt.label}
                                    {opt.disabled && ` (${t('calendar.requiresSprint')})`}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Color by. */}
                <div className="space-y-2">
                    <Label htmlFor="calendar-colorBy" className="text-xs font-medium">
                        {t('calendar.colorByLabel')}
                    </Label>
                    <Select
                        value={config.colorBy}
                        onValueChange={(v) => onConfigChange({ ...config, colorBy: v })}
                    >
                        <SelectTrigger id="calendar-colorBy" className="h-8">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {colorByOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label.startsWith('calendar.')
                                        ? t(opt.label as any)
                                        : opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* T21 R4: Periods section. The user defines named
                    date ranges that render as sprint-band-style
                    highlights on the calendar grid (similar to how
                    sprints are rendered). */}
                <PeriodsSection
                    periods={config.periods ?? []}
                    onChange={(periods) => onConfigChange({ ...config, periods })}
                />

                <div className="pt-2 flex justify-end">
                    {/* T21 R4: no Close button — clicking outside the
                        popover (via Radix's DismissableLayer) closes
                        it, and the popover also auto-closes when the
                        toolbar's settings trigger is re-clicked. The
                        explicit Close button was redundant. */}
                </div>
                <Popover.Arrow className="fill-surface drop-shadow-sm" />
            </Popover.Content>
        </Popover.Portal>
    );
}

// ============================================
// PERIODS SECTION
// ============================================
//
// Renders the editor for the `periods: PeriodDef[]` config field.
// Each period has a name + start date + end date + optional color.
// The list is editable inline: add via the "+ Add period" button,
// remove via the trash icon on each row. All edits are committed
// through `onChange(periods)` so the parent can persist them via
// `onConfigChange`.

interface PeriodsSectionProps {
    periods: PeriodDef[];
    onChange: (periods: PeriodDef[]) => void;
}

const DEFAULT_PERIOD_COLORS = [
    '#a855f7',
    '#6366f1',
    '#ec4899',
    '#f59e0b',
    '#10b981',
    '#ef4444',
];

function PeriodsSection({ periods, onChange }: PeriodsSectionProps) {
    const { t } = useI18n();
    const [draftName, setDraftName] = useState('');
    const [draftStart, setDraftStart] = useState('');
    const [draftEnd, setDraftEnd] = useState('');

    const canAdd =
        draftName.trim().length > 0 &&
        draftStart.length > 0 &&
        draftEnd.length > 0 &&
        draftStart <= draftEnd;

    const handleAdd = () => {
        if (!canAdd) return;
        const next: PeriodDef[] = [
            ...periods,
            {
                id: `period-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                name: draftName.trim(),
                startDate: draftStart,
                endDate: draftEnd,
                color: DEFAULT_PERIOD_COLORS[periods.length % DEFAULT_PERIOD_COLORS.length],
            },
        ];
        onChange(next);
        setDraftName('');
        setDraftStart('');
        setDraftEnd('');
    };

    const handleRemove = (id: string) => {
        onChange(periods.filter((p) => p.id !== id));
    };

    const handleEdit = (id: string, patch: Partial<PeriodDef>) => {
        onChange(periods.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">{t('calendar.periodsLabel')}</Label>
                {periods.length > 0 && (
                    <button
                        type="button"
                        className="text-[10px] text-foreground-muted hover:text-foreground underline-offset-2 hover:underline"
                        onClick={() => onChange([])}
                    >
                        {t('calendar.periodClearAll')}
                    </button>
                )}
            </div>

            {periods.length > 0 && (
                <ul className="space-y-1.5" data-testid="calendar-periods-list">
                    {periods.map((p) => (
                        <li
                            key={p.id}
                            className="flex items-start gap-2 p-2 rounded border border-white/10 bg-white/[0.02]"
                            data-testid={`calendar-period-row-${p.id}`}
                        >
                            <input
                                type="color"
                                value={p.color ?? DEFAULT_PERIOD_COLORS[0]}
                                onChange={(e) => handleEdit(p.id, { color: e.target.value })}
                                aria-label={t('calendar.periodColor')}
                                className="h-6 w-8 mt-1 shrink-0 rounded border border-white/10 bg-transparent cursor-pointer"
                            />
                            <div className="flex-1 min-w-0 space-y-1">
                                <Input
                                    value={p.name}
                                    onChange={(e) => handleEdit(p.id, { name: e.target.value })}
                                    placeholder={t('calendar.periodNamePlaceholder') as string}
                                    className="h-7 text-xs"
                                />
                                <div className="flex items-center gap-1">
                                    <Input
                                        type="date"
                                        value={p.startDate}
                                        onChange={(e) =>
                                            handleEdit(p.id, { startDate: e.target.value })
                                        }
                                        aria-label={t('calendar.periodStart') as string}
                                        className="h-7 text-xs flex-1 min-w-0"
                                    />
                                    <span className="text-foreground-muted text-xs shrink-0">
                                        →
                                    </span>
                                    <Input
                                        type="date"
                                        value={p.endDate}
                                        onChange={(e) =>
                                            handleEdit(p.id, { endDate: e.target.value })
                                        }
                                        aria-label={t('calendar.periodEnd') as string}
                                        className="h-7 text-xs flex-1 min-w-0"
                                    />
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemove(p.id)}
                                aria-label={t('calendar.periodRemove') as string}
                                className="h-7 w-7 shrink-0 text-foreground-muted hover:text-destructive"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}

            <div className="rounded border border-dashed border-white/10 p-2 space-y-1.5">
                <Input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    placeholder={t('calendar.periodNamePlaceholder') as string}
                    className="h-7 text-xs"
                />
                <div className="flex items-center gap-1">
                    <Input
                        type="date"
                        value={draftStart}
                        onChange={(e) => setDraftStart(e.target.value)}
                        aria-label={t('calendar.periodStart') as string}
                        className="h-7 text-xs flex-1 min-w-0"
                    />
                    <span className="text-foreground-muted text-xs shrink-0">→</span>
                    <Input
                        type="date"
                        value={draftEnd}
                        onChange={(e) => setDraftEnd(e.target.value)}
                        aria-label={t('calendar.periodEnd') as string}
                        className="h-7 text-xs flex-1 min-w-0"
                    />
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAdd}
                    disabled={!canAdd}
                    className="w-full h-7 text-xs justify-start"
                >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t('calendar.periodAdd')}
                </Button>
            </div>
            <p className="text-[10px] text-foreground-muted">{t('calendar.periodsHint')}</p>
        </div>
    );
}